import Backbone from "backbone";
import Parse from "parse";
import _ from "underscore";
import SkyObjectObservation from "../helpers/SkyObjectObservation";
import BaseParseObject from "./BaseParseObject";
import ObservingListAux from "./ObservingListAux";

("use strict");

const MAX_SKYOBJECTS_PER_LIST = 400;

export const ObservingList = BaseParseObject.extend(
	"ObservingList",
	{
		auxLists: [],

		validSortOrder: (sortOrder) =>
			!(_.isUndefined(sortOrder) || _.isEmpty(sortOrder)),

		// List-level sortOrder methods (for ordering lists in the list view)
		getSortOrder: function () {
			return this.get("sortOrder");
		},

		setSortOrder: function (order) {
			this.set("sortOrder", order);
		},

		updateSortOrder: async function (order) {
			this.set("sortOrder", order);
			return this.save();
		},

		saveList: async (list) => {
			const savedItem = await list.save().catch((error) => {
				Backbone.trigger("errors:parseReturned", error);
				return false;
			});

			if (!savedItem) return false;
			Backbone.trigger("observingLists:targetsUpdated", savedItem);
			return true;
		},

		updateAccess: function () {
			const acl = new Parse.ACL();
			acl.setPublicReadAccess(false);
			acl.setReadAccess(Parse.User.current().id, true);
			acl.setWriteAccess(Parse.User.current().id, true);
			this.setACL(acl);
		},

		setAttributes: function (data) {
			if (data?.title) {
				this.set("title", data.title);
			}

			if (data?.listDescription) {
				this.set("listDescription", data.listDescription);
			}

			if (data?.skyObjects) {
				this.set("skyObjects", data.skyObjects);
			}

			this.set("user", Parse.User.current());
		},

		getReferencedMessage: (observedCount) => {
			let message =
				`This list is still referenced by ${observedCount} observation`;
			if (observedCount > 1) message += "s";
			message +=
				". Would you like to delete it anyway and null out any observation references?";

			return message;
		},

		getNumberOfSkyObjects: function () {
			let count = 0;

			if (
				(this.has("skyObjects") && this.get("skyObjects")) ||
				this.auxLists.length > 0
			) {
				count = this.get("skyObjects")
					? Object.keys(this.get("skyObjects")).length
					: 0;
				this.auxLists.forEach((list) => {
					if (list.get("skyObjects")) {
						count += Object.keys(list.get("skyObjects")).length;
					}
				});
			}

			return count;
		},

		isListOpen: (skyObjects = {}) => {
			const numOfSkyObjects = Object.keys(skyObjects).length;

			return (
				numOfSkyObjects < MAX_SKYOBJECTS_PER_LIST
			);
		},

		createNewAuxList: function () {
			const newAuxList = new ObservingListAux();
			newAuxList.setDefaults({ observingList: this });

			return newAuxList;
		},

		getOpenAuxList: function () {
			if (this.auxLists.length > 0) {
				const lastAuxList = this.auxLists[this.auxLists.length - 1];
				const skyObjects = lastAuxList.get("skyObjects");

				if (skyObjects && this.listIsFull(skyObjects)) {
					return this.createNewAuxList();
				}

				return lastAuxList;
			}

			return this.createNewAuxList();
		},

		resetSortOrder: (skyObjects, offset) => {
			if (!skyObjects || Object.keys(skyObjects).length === 0) return {};

			const newSkyObjects = {};
			let objectIndex = 0;

			_.each(skyObjects, (skyObject) => {
				newSkyObjects[objectIndex] = skyObject;
				skyObject.sortOrder = parseInt(offset + objectIndex, 10);
				objectIndex++; // DO NOT use the _.each(object, index) "index" param here, as it will be the "key", not an actual index
			});

			return newSkyObjects;
		},

		getAllSkyObjects: function () {
			const allSkyObjects = {};
			const skyObjects = this.get("skyObjects");
			const sortedSkyObjects = this.resetSortOrder(skyObjects, 0);

			_.extend(allSkyObjects, sortedSkyObjects);

			for (let i = 0, len = this.auxLists.length; i < len; i++) {
				const offset =
					(i + 1) * MAX_SKYOBJECTS_PER_LIST;
				const auxSkyObjects = this.auxLists[i].get("skyObjects");
				const sortedAuxSkyObjects = this.resetSortOrder(auxSkyObjects, offset);

				// Auxiliary records reuse keys starting at zero. Merge by the
				// list-wide sort order so they cannot overwrite earlier blocks.
				_.each(sortedAuxSkyObjects, (skyObject) => {
					allSkyObjects[skyObject.sortOrder] = skyObject;
				});
			}

			return allSkyObjects;
		},

		addSkyObjectToList: function (skyObject) {
			skyObject.sortOrder = this.getNumberOfSkyObjects();

			const openList = this.isListOpen(this.get("skyObjects"))
				? this
				: this.getOpenAuxList();
			const skyObjects = openList.get("skyObjects");
			const sortedSkyObjects = this.resetSortOrder(skyObjects);
			sortedSkyObjects[Object.keys(sortedSkyObjects).length] = skyObject;
			openList.set("skyObjects", sortedSkyObjects);

			return this.saveList(openList);
		},

		removeSkyObjectFromList: function (list, sortOrder) {
			if (!this.validSortOrder(sortOrder)) return;

			const skyObjects = list.get("skyObjects") || {};
			const skyObjectToRemove = _.find(
				skyObjects,
				(skyObject) => +skyObject.sortOrder === +sortOrder,
			);

			if (!skyObjectToRemove) return false;

			const newSkyObjectsArray = _.reject(
				list.get("skyObjects"),
				(skyObject) => +skyObject.sortOrder === +sortOrder,
			);

			const newSkyObjects = this.resetSortOrder(newSkyObjectsArray, 0);
			list.set("skyObjects", newSkyObjects);

			return this.saveList(list);
		},

		removeSkyObject: async function (sortOrder) {
			if (!this.validSortOrder(sortOrder)) return false;

			let skyObjectWasRemoved = await this.removeSkyObjectFromList(
				this,
				sortOrder,
			);
			if (!skyObjectWasRemoved) {
				for (let i = 0, len = this.auxLists.length; i < len; i++) {
					skyObjectWasRemoved = this.removeSkyObjectFromList(
						this.auxLists[i],
						sortOrder,
					);

					if (skyObjectWasRemoved) break;
				}
			}

			return skyObjectWasRemoved;
		},

		update: function (data, updateAccess) {
			if (!this.get("ACL") || updateAccess) this.updateAccess();

			this.setAttributes(data);

			return this.saveList(this);
		},

		deleteAuxObservingList: function () {
			const auxObservingLists = this.auxLists;

			_.each(auxObservingLists, (auxList) => {
				auxList.delete();
			});
		},

		deleteAndSave: async function () {
			this.set("deleted", true);
			const deleted = await this.saveList(this);

			if (deleted) {
				this.deleteAuxObservingList();
			}
		},

		delete: function () {
			const observedCount =
				SkyObjectObservation.getSkyObjectObservationsByPointerId(
					this.id,
					"observingList",
				).length;

			if (observedCount === 0) return this.deleteAndSave();

			const message = this.getReferencedMessage(observedCount);


			Backbone.trigger("warning:askuser", message, () => {
				this.set("nullObservationReferences", true);
				this.deleteAndSave();
			});
		},
	},
);

export default ObservingList;
